import cx from 'classnames';
import { ELEMENT_IDS } from 'constants/dom-element.const';
import { resourceUrl } from 'helpers/url.helper';
import { filter, map, round } from 'lodash';
import { LayoutPhotoModel } from 'models/photo/layout.model';
import React, {
  FC,
  HtmlHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useBoothAppContext } from '../../context/BoothAppContext';
import {
  setBackgroundImg,
  setCurrentBackgroundId,
  setSelfeAppStateAction,
} from '../../store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from '../../store/store-hooks';
import Sticker from '../sticker/Sticker';
import PhotoFilter from './PhotoFilter';
import { OFFSET_PER } from './photo-sheet-utils';
import './photo-sheet.css';

export type PhotoSheetProps = HtmlHTMLAttributes<HTMLDivElement> & {
  isSelectSticker?: boolean;
  data?: UndefinedAll<LayoutPhotoModel>;
  filterValues?: number[];
  imageClick?: (imgIndex: number) => void;
  showPhotoIndex?: boolean;
  QRCodeProps?: React.ImgHTMLAttributes<HTMLImageElement>;
  ImageSheetProps?: React.ImgHTMLAttributes<HTMLImageElement>;
  PictureProps?: React.ImgHTMLAttributes<HTMLImageElement>;
  forwardedRef?: React.MutableRefObject<HTMLInputElement | null>;
  setListSelectSticker?: (frameId: string) => void;
  handleSetTargetFrames?: () => void;
};

function getPercentSize(s1: number, s2: number, offset: number = 0): string {
  return `${round(!s2 ? 0 : (s1 / s2) * 100, 2) + offset}%`;
}

const PhotoSheet: FC<PhotoSheetProps> = ({
  data,
  filterValues,
  imageClick,
  showPhotoIndex = false,
  QRCodeProps,
  ImageSheetProps,
  PictureProps,
  setListSelectSticker,
  isSelectSticker = false,
  handleSetTargetFrames,
  ...rest
}) => {
  const refTarget = useRef<any>(null);
  const dispatch = useAppDispatch();
  const { listBackgroundPhotos } = useBoothAppContext();
  const { backgroundId, imgBgUrl, currentBackgroundId } = useAppSelector(
    (state) => state.selfeApp,
  );

  const backgroundURL = useMemo(
    () =>
      filter(listBackgroundPhotos?.data, (i) => i.id === backgroundId)[0]
        ?.imageUrl,
    [backgroundId, listBackgroundPhotos?.data],
  );

  useEffect(() => {
    if (backgroundId !== currentBackgroundId) {
      dispatch(setBackgroundImg(backgroundURL));
      dispatch(setCurrentBackgroundId(backgroundId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundId, currentBackgroundId]);

  useEffect(() => {
    dispatch(setSelfeAppStateAction({ photoContainer: refTarget?.current }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      {...rest}
      ref={refTarget}
      id={ELEMENT_IDS.PHOTO_SHEET}
      className={cx('pb-photo-sheet', rest?.className)}
    >
      <>
        {map(data?.pictures, (item, index) => {
          const pictureSourceIndex = Math.max(item?.pictureTarget - 1, 0);
          const pictureSource = item?.isMiniPicture
            ? data?.pictures?.[pictureSourceIndex]
            : item;
          const itemClickIndex = item?.isMiniPicture
            ? pictureSourceIndex
            : index; // lấy index ảnh gốc được liên kết

          if (!pictureSource?.image) return null;

          return (
            <PhotoFilter
              id={`filter-${index}`}
              imageAlt="pic-of-sheet"
              imageUrl={pictureSource?.image}
              onClick={() => imageClick?.(itemClickIndex)}
              filterValues={filterValues}
              showPhotoIndex={showPhotoIndex}
              photoIndex={itemClickIndex + 1}
              {...PictureProps}
              style={{
                ...PictureProps?.style,
                left: getPercentSize(item?.x, data?.width || 0, -OFFSET_PER),
                top: getPercentSize(item?.y, data?.height || 0, -OFFSET_PER),
                width: getPercentSize(
                  item?.width,
                  data?.width || 0,
                  2 * OFFSET_PER,
                ),
                height: getPercentSize(
                  item?.height,
                  data?.height || 0,
                  2 * OFFSET_PER,
                ),
              }}
              rotate={`${pictureSource?.rotate}deg`}
              flip={`${pictureSource?.flip || 1}`}
              className={cx(
                'pb-photo-sheet-picture-item',
                PictureProps?.className,
              )}
              backgroundURL={imgBgUrl}
            />
          );
        })}
        <img
          alt="sheet"
          src={resourceUrl(data?.image)}
          {...ImageSheetProps}
          className={cx(
            'pb-photo-sheet-theme-image',
            ImageSheetProps?.className,
          )}
        />
        {data?.QRCode?.image && (
          <img
            alt="qr-code-of-sheet"
            src={data?.QRCode?.image}
            {...QRCodeProps}
            style={{
              ...QRCodeProps?.style,
              top: data?.QRCode?.y,
              left: data?.QRCode?.x,
            }}
            className={cx('pb-photo-sheet-qr-code', QRCodeProps?.className)}
          />
        )}
      </>
      {!showPhotoIndex && (
        <Sticker
          isSelectSticker={isSelectSticker}
          className="absolute top-0 left-0 w-full h-full"
        />
      )}
    </div>
  );
};

export default PhotoSheet;
